import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from '../components/Sidebar/Sidebar';
import TopNav from '../components/TopNav/TopNav';
import MobileSidebar from '../components/MobileSidebar/MobileSidebar';
import RightSidebar from '../components/RightSidebar/RightSidebar';
import BottomNav from '../components/BottomNav/BottomNav';
import { useTheme } from '../contexts/ThemeContext';
import { ChevronRight, Home } from 'lucide-react';

export default function DashboardLayout() {
  const { isRTL } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      if (width < 1024) {
        setIsSidebarCollapsed(true);
      } else {
        setIsSidebarCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Page transition variants
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 20,
    },
    animate: {
      opacity: 1,
      x: 0,
    },
    exit: {
      opacity: 0,
      x: -20,
    }
  };

  const pageTransition = {
    type: "tween",
    ease: [0.16, 1, 0.3, 1],
    duration: 0.3
  };

  // Breadcrumb route map
  const routeLabels = {
    '/dashboard': 'Overview',
    '/cv-builder': 'CV Builder',
    '/cv-generation': 'CV Generation',
    '/job-search': 'Job Search',
    '/job-match': 'Job Match',
    '/remote-jobs': 'Remote Jobs',
    '/job-library': 'Job Library',
    '/advanced-search': 'Advanced Search',
    '/cover-letter': 'Cover Letters',
    '/chatbot': 'AI Assistant',
    '/subscription': 'Subscription',
    '/settings': 'Settings',
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const crumbs = [{ label: 'Home', to: '/dashboard' }];
    // Match the longest route key that is a prefix of current path
    const sortedKeys = Object.keys(routeLabels).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (path.startsWith(key)) {
        if (key !== '/dashboard') crumbs.push({ label: routeLabels[key], to: key });
        // If we're deeper (e.g. /cv-builder/edit/123), add a "Edit" crumb
        if (path !== key && path.includes('/edit/')) crumbs.push({ label: 'Edit Resume', to: null });
        break;
      }
    }
    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();
  const showBreadcrumbs = location.pathname !== '/dashboard';

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-row h-screen overflow-y-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Sidebar */}
      {!isMobile && (
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
        />
      )}
      {isMobile && (
        <MobileSidebar
          isOpen={isMobileSidebarOpen}
          setIsOpen={setIsMobileSidebarOpen}
        />
      )}

      {/* Main Container - Single Scroll for Everything */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Top Navigation - Fixed */}
        <TopNav
          onOpenRightSidebar={() => setIsRightSidebarOpen(!isRightSidebarOpen)}
          onOpenMobileSidebar={() => setIsMobileSidebarOpen(true)}
          isRTL={isRTL}
        />

        {/* Breadcrumb Bar - Compact */}
        {showBreadcrumbs && (
          <div className="px-4 md:px-6 py-1.5 border-b border-[#E5E7EB]/60 bg-white/60 backdrop-blur-sm flex items-center gap-1.5 text-xs text-[#6B7280]" dir={isRTL ? 'rtl' : 'ltr'}>
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <ChevronRight className="w-3 h-3 text-[#D1D5DB] flex-shrink-0" />}
                {idx === 0 ? (
                  <Link to="/dashboard" className="flex items-center gap-1 hover:text-[#2F80ED] transition-colors">
                    <Home className="w-3 h-3" />
                    <span className="hidden sm:inline">{crumb.label}</span>
                  </Link>
                ) : crumb.to ? (
                  <Link to={crumb.to} className="hover:text-[#2F80ED] transition-colors font-medium truncate max-w-[160px]">{crumb.label}</Link>
                ) : (
                  <span className="text-[#333333] font-semibold truncate max-w-[160px]">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Content Area - Single Unified Scroll (disabled for Chatbot to prevent nested scrolling) */}
        <div className={`flex-1 ${location.pathname.startsWith('/chatbot') ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'} overflow-x-hidden professional-scrollbar`} dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Main Page Content */}
          <div className={`w-full ${location.pathname.startsWith('/chatbot') ? 'h-full flex flex-col flex-1' : ''}`}>
            <AnimatePresence mode="sync" initial={false}>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                className={location.pathname.startsWith('/chatbot') ? 'h-full flex flex-col flex-1' : ''}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <BottomNav 
          onOpenMobileSidebar={() => setIsMobileSidebarOpen(true)} 
          onOpenRightSidebar={() => setIsRightSidebarOpen(true)} 
        />

        {/* Right Sidebar - Dropdown from top (doesn't affect layout) */}
        <AnimatePresence>
          {isRightSidebarOpen && (
            <>
              {/* Backdrop overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsRightSidebarOpen(false)}
                className="fixed inset-0 bg-black/20 z-40"
              />
              
              {/* Sidebar - Dropdown panel from top-right */}
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className={`fixed top-16 ${isRTL ? 'left-4' : 'right-4'} w-[340px] max-h-[calc(100vh-5rem)] bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-[#E5E7EB]`}
              >
                <div className="h-full overflow-y-auto professional-scrollbar">
                  <RightSidebar 
                    isOpen={isRightSidebarOpen} 
                    onClose={() => setIsRightSidebarOpen(false)} 
                  />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
